import { useState } from 'react';
import invariant from 'invariant';

import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { isPromise } from 'dpl/shared/utils';
import { isPlain } from 'dpl/shared/utils/object';
import { getStructuredServerErrorsFromResponseJson } from 'dpl/shared/utils/getStructuredServerErrorsFromResponseJson';
import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/shared/constants/shared';
import { OTP_REQUIRED_ERROR_CODE } from 'dpl/shared/fetching/utils/constants';

import { normalizeServerErrors } from '../utils';
import useValidation from './useValidation';
import useFormState from './useFormState';

type FormState = Record<string, any>;
type FormErrors = Record<string, string[]>;
type SetValueCallback = (formState: FormState) => void;

interface SubmissionError extends Error {
  response: Response;
}

interface UseFormOptions {
  defaultFormState?: FormState;
  onSubmit?: ((formState: FormState, ...args: any[]) => any) | null;
  submitSuccessMessages?: string[];
  mapFormStateToValidationSchema?: (formState: FormState) => any;
}

const DEFAULT_OPTIONS: Required<UseFormOptions> = {
  defaultFormState: {},
  onSubmit: null,
  submitSuccessMessages: [],
  mapFormStateToValidationSchema: () => null
};

export interface UseFormData {
  handleSubmission: (...args: any[]) => Promise<any>;
  formState: FormState;
  setValue: (path: string, value: any, cb?: SetValueCallback) => FormState;
  pushValue: (path: string, value: any, cb?: SetValueCallback) => FormState;
  removeValue: (path: string, cb?: SetValueCallback) => FormState;
  isValid: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  batchSetValue: (obj: Record<string, any>, cb?: SetValueCallback) => FormState;
  reset: (state?: FormState) => FormState;
  validate: () => void;
  formErrors: FormErrors;
  handleFieldChange: (e: { target: { name: string; value: any } }) => FormState;
  didSubmissionFail: boolean;
  didSubmissionSucceed: boolean;
  isValidating: boolean;
  setServerErrors: (errors: FormErrors) => void;
  handleFormFieldChange: (e: {
    target: { name: string; value: any };
  }) => FormState;
  addToFormState: (
    path: string,
    value: any,
    cb?: SetValueCallback
  ) => FormState;
  pushToFormState: (
    path: string,
    value: any,
    cb?: SetValueCallback
  ) => FormState;
  contextValue: UseFormData;
}

export default function useForm(opts: UseFormOptions = {}): UseFormData {
  const options = { ...DEFAULT_OPTIONS, ...opts };

  const [serverErrors, setServerErrors] = useState<FormErrors>({});
  const [didSubmissionFail, setDidSubmissionFail] = useState(false);
  const [didSubmissionSucceed, setDidSubmissionSucceed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formState,
    setValue,
    removeValue,
    pushValue,
    reset,
    isDirty,
    batchSetValue
  } = useFormState(options.defaultFormState);

  const {
    isValidating,
    isValid,
    errors: clientErrors,
    validate
  } = useValidation(options.mapFormStateToValidationSchema);

  const [_setValue, _removeValue, _pushValue, _reset, _batchSetValue] = [
    setValue,
    removeValue,
    pushValue,
    reset,
    batchSetValue
  ].map(func => (...args: any[]) => {
    const newState = func(...args);
    validate(newState);
    setServerErrors({});
    setDidSubmissionFail(false);
    return newState;
  });

  const { pushToastNotification, clearToastNotifications, toastNotifications } =
    useToastNotifications();

  const formErrors: FormErrors = { ...clientErrors, ...serverErrors };

  function validateFormState() {
    validate(formState);
  }

  function handleFieldChange(e: { target: { name: string; value: any } }) {
    const { name, value } = e.target;
    return _setValue(name, value);
  }

  function handleSubmission(...args: any[]) {
    return new Promise((resolve, reject) => {
      invariant(
        typeof options.onSubmit === 'function',
        'handleSubmission was called without providing an onSubmit option'
      );

      if (isSubmitting) {
        window.console.warn(
          'handleSubmission called when request is already in flight'
        );
        resolve(undefined);

        return;
      }

      setIsSubmitting(true);

      const toastErrors = toastNotifications.filter(
        (n: any) => n.type === TOAST_NOTIFICATIONS_TYPES.ERROR
      );

      if (toastErrors.length > 0) {
        clearToastNotifications();
      }

      const ret = options.onSubmit!(formState, ...args);

      isPromise(ret) ? ret.then(resolve).catch(reject) : resolve(ret);
    })
      .then(ret => {
        if (
          ret &&
          typeof ret === 'object' &&
          'response' in ret &&
          !(ret as any).response.ok
        ) {
          const err = new Error('Form submission failed') as SubmissionError;
          err.response = (ret as any).response;

          throw err;
        }

        options.submitSuccessMessages.forEach(e =>
          pushToastNotification({
            message: e,
            type: TOAST_NOTIFICATIONS_TYPES.SUCCESS
          })
        );

        setIsSubmitting(false);
        setDidSubmissionSucceed(true);

        return ret;
      })
      .catch((err: SubmissionError) => {
        setIsSubmitting(false);
        setDidSubmissionFail(true);

        if (
          'response' in err &&
          err.response.headers
            .get('content-type')
            ?.startsWith('application/json')
        ) {
          return err.response
            .clone()
            .json()
            .then((json: any) => {
              if (json.error_code === OTP_REQUIRED_ERROR_CODE) {
                // An OTP step-up is required -- not a form failure. Leave the
                // form untouched and resolve with the error so the caller (the
                // OTP gate) can open the step-up modal.
                return err;
              }

              if (json.errors) {
                setServerErrors(normalizeServerErrors(json.errors, formState));
              }

              if (isPlain(json.error_messages)) {
                setServerErrors(
                  normalizeServerErrors(json.error_messages, formState)
                );
              }

              getStructuredServerErrorsFromResponseJson(json).forEach(
                (message: string) =>
                  pushToastNotification({
                    message,
                    type: TOAST_NOTIFICATIONS_TYPES.ERROR
                  })
              );

              return err;
            });
        }

        throw err;
      });
  }

  const contextValue = {
    setValue: _setValue,
    removeValue: _removeValue,
    pushValue: _pushValue,
    reset: _reset,
    batchSetValue: _batchSetValue,
    formErrors,
    formState,
    handleFieldChange,
    handleSubmission,
    isDirty,
    didSubmissionFail,
    didSubmissionSucceed,
    isSubmitting,
    isValidating,
    isValid,
    validate: validateFormState,
    setServerErrors,

    handleFormFieldChange: handleFieldChange,
    addToFormState: _setValue,
    pushToFormState: _pushValue
  } as UseFormData;

  return {
    ...contextValue,
    contextValue
  };
}
